// Authenticated-user gate. Blocks the entire app tree until Kinde has
// finished its handshake AND the user's org is in our role registry,
// then exposes the user + role + logout function via ``UserContext``.
// Mirrors ``admin-app/src/auth/UserContext.tsx``.
//
// On first render, if Kinde reports no user we fire ``login()``. It names
// no org, so someone outside InRange signs in with whatever org they have,
// or none, and is an ``external`` viewer whom the backend admits only to the
// pages shared with them (docs/Y2026-Q4/design-amazon-overview-sharing.md).
// A browser that has signed in as staff asks for the InRange org from then
// on, so a staff member in several orgs isn't shown Kinde's org picker each
// time. See web/src/auth/roles.ts for the org -> role registry.
//
// The path the user was on before sign-in is preserved in
// ``app_state.redirectTo`` and read back by ``LoginRedirect.tsx``
// after the post-login callback lands.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, type ReactNode } from "react";

import { forgetViewAs, viewingAs } from "../api/viewAs";
import { accessTokenIsMissingEmail } from "./accessTokenClaims";
import { KINDE_CONFIG } from "./kindeConfig";
import { roleForOrgCode } from "./roles";
import { UserContext } from "./UserContext";

// One-shot guard so a token that somehow still lacks `email` after a re-auth
// (misconfig, or a principal Kinde never emits it for) can't loop the sign-out.
// sessionStorage, not localStorage: we want the check to run afresh in a new
// tab/session, just not twice within one.
const STALE_TOKEN_REAUTH_KEY = "inrange-agents-reauth-missing-email-claim";

// Set once this browser has signed in as staff.
const STAFF_SIGN_IN_KEY = "inrange-agents-staff-sign-in";

const staffSignIn = (): boolean => {
  try {
    return localStorage.getItem(STAFF_SIGN_IN_KEY) === "1";
  } catch {
    return false;
  }
};

const rememberStaffSignIn = (): void => {
  try {
    localStorage.setItem(STAFF_SIGN_IN_KEY, "1");
  } catch {
    // Storage blocked: the next sign-in names no org, which still works.
  }
};

const forgetStaffSignIn = (): void => {
  try {
    localStorage.removeItem(STAFF_SIGN_IN_KEY);
  } catch {
    // Storage blocked: nothing was remembered either.
  }
};

const Loading = ({ label }: { label: string }) => (
  <div
    style={{
      padding: "2rem",
      fontFamily: "var(--font-sans, system-ui)",
      color: "var(--ir-navy, #1a2244)",
    }}
  >
    {label}
  </div>
);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { isLoading, user, login, logout, getOrganization, getToken } = useKindeAuth();

  // Fire login() once auth finishes loading if there's no user yet.
  // ``useEffect`` rather than an inline call so React doesn't trigger
  // an infinite re-render on the redirect.
  useEffect(() => {
    if (!isLoading && !user) {
      login({
        ...(staffSignIn() ? { org_code: KINDE_CONFIG.orgCode } : {}),
        app_state: {
          redirectTo: window.location.pathname + window.location.search,
        },
      });
    }
  }, [isLoading, user, login]);

  // Once signed in, make sure the ACCESS token carries the ``email`` claim the
  // BE reads for operator attribution (principle 27). A token issued before the
  // Kinde app was configured to emit ``email`` on the access token lacks it;
  // force a one-time re-auth to pick up a fresh token rather than leaving the
  // operator stamping the opaque ``kp_`` sub for up to 24h until the silent
  // refresh lands. Async (getToken returns a promise), so an effect rather than
  // the inline render gate below; the one-shot sessionStorage guard stops any
  // sign-out loop. See docs/Y2026-Q3/design-operator-actor-attribution.md.
  useEffect(() => {
    if (isLoading || !user) return;
    if (sessionStorage.getItem(STALE_TOKEN_REAUTH_KEY)) return;
    let cancelled = false;
    void getToken().then((token) => {
      if (cancelled || !accessTokenIsMissingEmail(token)) return;
      sessionStorage.setItem(STALE_TOKEN_REAUTH_KEY, "1");
      logout();
    });
    return () => {
      cancelled = true;
    };
  }, [isLoading, user, getToken, logout]);

  if (isLoading) {
    return <Loading label="Loading…" />;
  }
  if (!user) {
    // The useEffect above is firing the login redirect; this render
    // is just a placeholder while the browser navigates away.
    return <Loading label="Signing in…" />;
  }

  // Staff are the InRange org with an ``@inrange.io`` email; an InRange-org
  // account without one is signed out (an accidental non-employee added to the
  // staff org). Everyone else is ``external``: the backend is the gate, admitting
  // them only to the pages shared with them. The ``setTimeout`` matches
  // admin-app's pattern - calling logout synchronously during render misbehaves.
  const organisation = getOrganization();
  const role = roleForOrgCode(organisation?.orgCode) ?? "external";
  const staffEmail = Boolean(user.email?.endsWith("@inrange.io"));
  // Staff signed in through another org (they belong to several): sign them in again,
  // this time asking for the InRange org.
  if (staffEmail && role !== "admin") {
    rememberStaffSignIn();
    setTimeout(() => logout(), 100);
    return <Loading label="Signing you in as InRange staff…" />;
  }
  if (role === "admin" && !staffEmail) {
    setTimeout(() => logout(), 100);
    return (
      <Loading label="Unauthorised - your account isn't in a recognised organisation. Signing out…" />
    );
  }
  if (role === "admin") rememberStaffSignIn();

  // Signing out forgets the staff sign-in, so the next person at this browser (a meeting
  // room's screen) can sign in from outside InRange.
  const signOut = () => {
    forgetStaffSignIn();
    forgetViewAs();
    logout();
  };

  // Staff in View as get the external app, read-only (api/viewAs.ts). Anyone else in this
  // tab drops what a staff member left, before a request carries it.
  if (role !== "admin") forgetViewAs();
  const viewAs = role === "admin" ? viewingAs() : null;
  return (
    <UserContext.Provider
      value={{ user, role: viewAs ? "external" : role, viewingAs: viewAs, logout: signOut }}
    >
      {children}
    </UserContext.Provider>
  );
};
